
import React, { useState } from 'react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Image, Upload } from "lucide-react";

interface ImageAttribution {
  photographer?: string;
  photographerUrl?: string;
}

interface BlogImageFieldsProps {
  coverImage: string;
  imageAttribution: ImageAttribution;
  onCoverImageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onImageAttributionChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled: boolean;
}

export const BlogImageFields = ({
  coverImage,
  imageAttribution,
  onCoverImageChange,
  onImageAttributionChange,
  disabled
}: BlogImageFieldsProps) => {
  const [showImagePreview, setShowImagePreview] = useState(true);

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="coverImage" className="text-base font-medium">Cover Image</Label>
        
        <div className="flex flex-col space-y-4">
          <div className="flex items-center gap-2">
            <Button 
              variant="outline"
              size="sm"
              className="relative px-3"
              disabled={disabled}
            >
              <Upload size={16} className="mr-2" />
              <span>Upload Image</span>
              <Input
                type="file"
                id="coverImage"
                onChange={onCoverImageChange}
                disabled={disabled}
                className="absolute inset-0 opacity-0 cursor-pointer"
                accept="image/*"
              />
            </Button>
            
            {coverImage && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowImagePreview(!showImagePreview)}
                type="button"
              >
                {showImagePreview ? 'Hide Preview' : 'Show Preview'}
              </Button>
            )}
          </div>
          
          {coverImage && showImagePreview && (
            <Card className="overflow-hidden">
              <CardContent className="p-2">
                <div className="relative aspect-video">
                  <img
                    src={coverImage}
                    alt="Cover Preview"
                    className="rounded-md object-cover w-full h-full"
                  />
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <div className="space-y-4 bg-slate-50 rounded-md p-4">
        <h3 className="font-medium flex items-center gap-2">
          <Image size={16} />
          Image Attribution
        </h3>
        
        <div>
          <Label htmlFor="photographer" className="text-sm">Photographer</Label>
          <Input
            type="text"
            id="photographer"
            name="photographer"
            value={imageAttribution.photographer}
            onChange={onImageAttributionChange}
            placeholder="Photographer Name"
            disabled={disabled}
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="photographerUrl" className="text-sm">Photographer URL</Label>
          <Input
            type="text"
            id="photographerUrl"
            name="photographerUrl"
            value={imageAttribution.photographerUrl}
            onChange={onImageAttributionChange}
            placeholder="https://example.com/photographer"
            disabled={disabled}
            className="mt-1"
          />
        </div>
      </div>
    </div>
  );
};
